import React, { useCallback, useEffect, useRef } from 'react';

import { View } from 'react-native';

import {
    Directions,
    FlingGestureHandler,
    State,
} from 'react-native-gesture-handler';
import Animated, {
    ReduceMotion,
    cancelAnimation,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withTiming,
} from 'react-native-reanimated';

import NotificationInfo from 'assets/images/svgs/Player/ico_notification_info.svg';

import {
    isTabletOrWeb,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles, { MAX_NOTIFICATION_WIDTH, TABLET_RIGHT_INDENT } from './styles';
import { AppNotification, Props } from './types';

const ANIMATION_DURATION = 300;
const MIN_POSITION = 0;

function Notification({
    notification,
    onResetNotification,
    customIcon,
    fromPlayer = false,
}: Props) {
    const prevNotification = useRef<AppNotification | undefined>(notification);

    const title = notification?.title;
    const description = notification?.description;

    const MAX_POSITION = fromPlayer
        ? widthPercentageToDP(340)
        : MAX_NOTIFICATION_WIDTH + TABLET_RIGHT_INDENT;

    const translate = useSharedValue(
        notification ? MIN_POSITION : MAX_POSITION,
    );

    const style = useAnimatedStyle(() => {
        return {
            transform: [
                {
                    translateX: translate.value,
                },
            ],
            opacity: interpolate(
                translate.value,
                [MIN_POSITION, MAX_POSITION],
                [1, 0],
            ),
        };
    });

    const handleStateChange = useCallback(
        ({ nativeEvent }) => {
            if (nativeEvent.state === State.ACTIVE) {
                translate.value = fromPlayer ? nativeEvent.x : nativeEvent.y;
            }

            if (nativeEvent.state === State.END) {
                translate.value = withTiming(
                    MAX_POSITION,
                    {
                        duration: ANIMATION_DURATION,
                        reduceMotion: ReduceMotion.Never,
                    },
                    () => {
                        runOnJS(onResetNotification)();
                    },
                );
            }
        },
        [onResetNotification, fromPlayer],
    );

    useEffect(() => {
        if (notification) {
            if (
                prevNotification.current &&
                prevNotification.current !== notification
            ) {
                cancelAnimation(translate);
                translate.value = withTiming(MAX_POSITION, {
                    duration: 0,
                });
            }

            translate.value = withTiming(
                MIN_POSITION,
                {
                    duration: ANIMATION_DURATION,
                    reduceMotion: ReduceMotion.Never,
                },
                () => {
                    translate.value = withDelay(
                        5000,
                        withTiming(
                            MAX_POSITION,
                            {
                                duration: ANIMATION_DURATION,
                                reduceMotion: ReduceMotion.Never,
                            },
                            (finished) => {
                                if (finished) {
                                    runOnJS(onResetNotification)();
                                }
                            },
                        ),
                        ReduceMotion.Never,
                    );
                },
            );
        }

        prevNotification.current = notification;
    }, [notification]);

    const containerStyle = () => {
        if (fromPlayer) {
            return styles.rootPlayer;
        }

        if (isTabletOrWeb) {
            return styles.rootTablet;
        }

        return styles.rootPhone;
    };

    return (
        <FlingGestureHandler
            direction={Directions.RIGHT}
            onHandlerStateChange={handleStateChange}
        >
            <Animated.View style={[styles.root, containerStyle(), style]}>
                {customIcon || <NotificationInfo />}
                <View style={styles.info}>
                    {!!title && (
                        <Text
                            bold
                            category={ETextCategory.body1}
                            color={ETextColor.high_contrast}
                            numberOfLines={isWeb ? 2 : 3}
                        >
                            {title}
                        </Text>
                    )}
                    {!!description && (
                        <Text
                            category={ETextCategory.caption1}
                            color={ETextColor.neutral}
                            style={styles.body}
                            numberOfLines={2}
                        >
                            {description}
                        </Text>
                    )}
                </View>
            </Animated.View>
        </FlingGestureHandler>
    );
}

export default Notification;
